import { Doc } from '@dadohr/shared/domainTypes/document';
import { AirtableIntegrationConfigV2 } from '@dadohr/shared/domainTypes/integrations/airtable';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useRef } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { OAuthSetupDialogV2 } from '../oauth';
import { removeAirtableConfig, useAirtableConfig } from './serviceV2';

const NAME = 'Airtable';

export const AIRTABLE_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'integrations_airtable-exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<AirtableIntegrationConfigV2>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Setup complete')}
        </Alert>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeAirtableConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const SetupDialogV2 = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<AirtableIntegrationConfigV2>) => {
  const { t } = useTranslation();
  const isSetup = !!(config?.data.accessToken && config?.data.refreshToken);
  const previousConfig = useRef<Doc<AirtableIntegrationConfigV2> | null>();

  //This is a bit of a hack to detect when the config active status changes so we can show the modal dialog
  //here even if the integration is completed on another browser tab.
  useEffect(() => {
    if (
      previousConfig.current?.data &&
      config?.data.active &&
      !previousConfig.current?.data.active
    ) {
      onSuccessfulIntegration();
    }
    previousConfig.current = config;
  }, [config, onSuccessfulIntegration]);

  if (!config) {
    return null;
  }

  if (isSetup) {
    return (
      <ExistingSetupDialog config={config} open={open} onClose={onClose} />
    );
  }

  return (
    <OAuthSetupDialogV2
      orgId={config.data.orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthCf={'integrations_airtable-oauthGetAuthorizeUrl'}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.airtable.setupInfo.p1')}
      </Alert>
    </OAuthSetupDialogV2>
  );
};

export const AirtableSettingsButtonV2 = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useAirtableConfig(org.id, userId);
  const cfg = config || null;

  return (
    <>
      {
        <IntegrationConfigurationButton
          name={NAME}
          functionalities={functionalities}
          logoUrl="/images/integrations-logos/airtable.svg"
          active={!!cfg?.data.active}
          interactive={interactive}
          renderSetupDialog={({
            open,
            onClose,
            onFailure,
            onSuccessfulIntegration
          }: IntegrationSetupDialogProperties) => (
            <SetupDialogV2
              config={cfg}
              open={open}
              onClose={onClose}
              onFailure={onFailure}
              onSuccessfulIntegration={onSuccessfulIntegration}
            />
          )}
        />
      }
    </>
  );
};
